import { chipInteractiveClasses } from "@busbud/horizon";

import type { Tracker } from "@app/tracking/tracker";
import { yieldToMain } from "@app/utils/scheduler-yield";

import {
  clickedRouteCalendarDeparture,
  clickedRouteCalendarMore,
  clickedRouteCalendarTab,
  showPriceCalendar
} from "../../tracking/price-calendar-tracking";

const TAB_NAV_CSS_SELECTOR = "[data-price-calendar-item]";
const TAB_PANE_CSS_SELECTOR = "[data-price-calendar-tab-pane]";
const MORE_LINK_CSS_SELECTOR = "[data-price-calendar-more-link]";
const PRICE_CALENDAR_CSS_SELECTOR = "[data-testid='price-calendar']";
export const DEPARTURE_LINK_CSS_SELECTOR =
  "[data-price-calendar-departure-link]";

const active_chip_classes = chipInteractiveClasses
  .root({
    variant: "outlined",
    appearance: "default",
    isDisabled: false
  })
  .split(" ");
const non_active_chip_classes = chipInteractiveClasses
  .root({
    variant: "subtle",
    appearance: "default",
    isDisabled: false
  })
  .split(" ");

export class PriceCalendarBehaviour {
  private tracker: Tracker | null = null;
  private tab_navs: HTMLElement[] = [];
  private tab_panes: HTMLElement[] = [];
  private more_buttons: HTMLElement[] = [];

  public init(tracker: Tracker, document: Document) {
    this.tracker = tracker;

    this.tab_navs = Array.from(document.querySelectorAll(TAB_NAV_CSS_SELECTOR));
    this.tab_panes = Array.from(
      document.querySelectorAll(TAB_PANE_CSS_SELECTOR)
    );
    this.more_buttons = Array.from(
      document.querySelectorAll(MORE_LINK_CSS_SELECTOR)
    );

    let content_index = 0;

    const price_calendar = document.querySelector(PRICE_CALENDAR_CSS_SELECTOR);
    const data_source = price_calendar?.getAttribute("data-data-source");
    const show_prices = price_calendar?.getAttribute("data-show-prices");

    this._handleInitialData(data_source || "unknown", show_prices === "true");

    for (const [index, button] of this.tab_navs.entries()) {
      const current_context_index = content_index;

      button.addEventListener("click", () =>
        this._handleTabNavClick(button, index, current_context_index)
      );

      const tab_pane = this.tab_panes[current_context_index];
      if (tab_pane) {
        this.setPaneListeners(tab_pane, index);
        content_index++;
      }
    }

    // Enabling tab pane listeners when tab_navs are disabled (can happen in some XPs)
    if (!this.tab_navs.length && this.tab_panes.length) {
      for (const [index, tab_pane] of this.tab_panes.entries()) {
        this.setPaneListeners(tab_pane, index);
      }
    }

    this.more_buttons.forEach(more_button => {
      more_button.addEventListener("click", () => {
        const index_of_current_selection = this.tab_navs.findIndex(tab_nav =>
          active_chip_classes.every(c => tab_nav.classList.contains(c))
        );
        this._handleMoreDeparturesClick(
          more_button,
          index_of_current_selection
        );
      });
    });
  }

  private setPaneListeners(tab_pane: HTMLElement, index: number) {
    const departure_buttons = Array.prototype.slice.call(
      tab_pane.querySelectorAll(DEPARTURE_LINK_CSS_SELECTOR)
    );
    departure_buttons.forEach(button => {
      const annotation = button.getAttribute("data-annotation");
      button.addEventListener("click", () =>
        this._handleDeparturesClick(button, index, annotation)
      );
    });
  }

  private _handleTabNavClick(
    button: HTMLElement,
    index: number,
    content_index: number
  ) {
    if (!this.tab_panes || !this.tab_navs) {
      return;
    }
    const tracking_event = clickedRouteCalendarTab(index);

    yieldToMain().then(() => {
      const previously_selected_date_time = this.tab_navs
        .find(tab_nav =>
          active_chip_classes.every(c => tab_nav.classList.contains(c))
        )
        ?.querySelector("time")?.dateTime;
      const new_selected_date_time = button.querySelector("time")?.dateTime;

      if (button.getAttribute("data-href")) {
        this.tracker?.track(
          tracking_event,
          () => (window.location.href = button.getAttribute("data-href") || "")
        );
      } else {
        this.tab_panes.forEach((item, i) => {
          item.classList.add("hidden");
          if (i === content_index) {
            item.classList.remove("hidden");
          }
        });
        this.tab_navs.forEach((item, i) => {
          if (i === index) {
            item.classList.remove(...non_active_chip_classes);
            item.classList.add(...active_chip_classes);
          } else {
            item.classList.remove(...active_chip_classes);
            item.classList.add(...non_active_chip_classes);
          }
        });

        this.more_buttons.forEach(more_button => {
          const previous_redirect_href = more_button.getAttribute("data-href");

          const new_redirect_href =
            previous_redirect_href?.replace(
              "=" + previously_selected_date_time,
              "=" + new_selected_date_time
            ) || "";
          more_button.setAttribute("data-href", new_redirect_href);
        });

        window.dispatchEvent(new CustomEvent("lazyload-refresh"));
        this.tracker?.asyncTrack(tracking_event);
      }
    });
  }

  private _handleMoreDeparturesClick(button: Element, index: number) {
    yieldToMain().then(() => {
      this.tracker?.track(
        clickedRouteCalendarMore(index),
        () => (window.location.href = button.getAttribute("data-href") || "")
      );
    });
  }

  private _handleDeparturesClick(
    button: Element,
    index: number,
    annotation?: string
  ) {
    yieldToMain().then(() => {
      this.tracker?.track(
        clickedRouteCalendarDeparture(index, annotation),
        () => (window.location.href = button.getAttribute("data-href") || "")
      );
    });
  }

  private _handleInitialData(data_source?: string, show_prices?: boolean) {
    yieldToMain().then(() => {
      this.tracker?.track(showPriceCalendar(data_source, show_prices));
    });
  }
}
